import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateDDMMYYYY } from "@/lib/format";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export const TasksAdmin = () => {
  const [tasks, setTasks] = useState([]);
  const [sites, setSites] = useState([]);
  const [guards, setGuards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ site_id: "", guard_id: "", title: "", description: "", due_at: "", priority: "MEDIUM" });

  const load = useCallback(async () => {
    const r = await api.get("/tasks");
    setTasks(r.data);
  }, []);

  useEffect(() => {
    load();
    api.get("/sites").then(r => setSites(r.data));
    api.get("/guards").then(r => setGuards(r.data));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", {
        ...form,
        guard_id: form.guard_id || null,
        due_at: new Date(form.due_at).toISOString(),
      });
      toast.success("Task assigned");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const siteName = (id) => sites.find(s => s.id === id)?.name || id;
  const guardName = (id) => guards.find(g => g.id === id)?.full_name || "All guards on site";

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-muted" style={{ fontSize: 14 }}>{tasks.filter(t => !t.completed).length} open · {tasks.filter(t => t.completed).length} done</div>
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="add-task">
          <Plus size={14} /> New task
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {tasks.map(t => (
          <div key={t.id} className="s-card" data-testid={`task-${t.id}`}>
            <div className="flex justify-between items-start">
              <div style={{ fontSize: 15, color: "#fff", textDecoration: t.completed ? "line-through" : "none" }}>{t.title}</div>
              <div className="flex gap-2">
                <StatusBadge status={t.priority} />
                {t.completed && <StatusBadge status="COMPLETED" label="Done" />}
              </div>
            </div>
            <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
              {siteName(t.site_id)} · {guardName(t.guard_id)} · due {formatDateDDMMYYYY(t.due_at)}
            </div>
            {t.description && <div className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>{t.description}</div>}
          </div>
        ))}
      </div>

      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="s-card" style={{ width: 420, maxWidth: "90vw" }}>
            <div className="h-section mb-4">New task</div>
            <label className="text-muted" style={{ fontSize: 12 }}>Title</label>
            <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="s-input mt-1 mb-3" />
            <label className="text-muted" style={{ fontSize: 12 }}>Description</label>
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="s-input mt-1 mb-3" rows={3} />
            <label className="text-muted" style={{ fontSize: 12 }}>Site</label>
            <select required value={form.site_id} onChange={(e) => setForm({...form, site_id: e.target.value})} className="s-input mt-1 mb-3">
              <option value="">Select...</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <label className="text-muted" style={{ fontSize: 12 }}>Guard (optional)</label>
            <select value={form.guard_id} onChange={(e) => setForm({...form, guard_id: e.target.value})} className="s-input mt-1 mb-3">
              <option value="">All guards on site</option>
              {guards.map(g => <option key={g.id} value={g.id}>{g.full_name}</option>)}
            </select>
            <label className="text-muted" style={{ fontSize: 12 }}>Due at</label>
            <input type="datetime-local" required value={form.due_at} onChange={(e) => setForm({...form, due_at: e.target.value})} className="s-input mt-1 mb-3" />
            <label className="text-muted" style={{ fontSize: 12 }}>Priority</label>
            <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} className="s-input mt-1 mb-4">
              {["LOW","MEDIUM","HIGH"].map(p => <option key={p}>{p}</option>)}
            </select>
            <button type="submit" className="btn-primary w-full">Assign task</button>
          </form>
        </div>
      )}
    </div>
  );
};
